import { ComponentType, lazy } from 'react';

const RELOADED_FOR = 'lazyPage.reloadedFor';

/**
 * A page loaded the first time it is shown, rather than with the application (#226).
 *
 * `React.lazy` wants a default export and these pages have named ones, so `load` resolves to
 * the component itself: `lazyPage(async () => (await import('./NewAssignment')).NewAssignment)`.
 *
 * A deploy replaces the chunk files, and a tab opened before it asks for ones that are gone. The
 * import then fails; the page reloads once, which fetches the new index and its chunks. A second
 * failure in the same tab is a real one, and goes to the nearest error boundary.
 */
export const lazyPage = <P extends object>(load: () => Promise<ComponentType<P>>) => lazy(async () => {
  try {
    const page = await load();
    sessionStorage.removeItem(RELOADED_FOR);
    return { default: page };
  } catch (error) {
    if (sessionStorage.getItem(RELOADED_FOR) !== window.location.pathname) {
      sessionStorage.setItem(RELOADED_FOR, window.location.pathname);
      window.location.reload();
      // Nothing to render: the page is going away.
      return new Promise<{ default: ComponentType<P> }>(() => undefined);
    }
    throw error;
  }
});
